import { ORGANIZATION_ROLES_LABELS } from "@common/constants";
import { toStringArray } from "@common/helpers/organizationMemberClientAccess";
import { pluralizeWithCount } from "@common/helpers/string";
import {
    AuditLogChangeType,
    AuditLogDiff,
    RefValue,
    ResourceType,
} from "@common/types/auditLog";
import { OrganizationUserRole } from "@common/userOrganizationRoles";
import { ActivityDescriptionParts } from "@components/history/utils/activityDescriptionTypes";
import { getActivityDescriptionParts } from "@components/history/utils/getActivityDescription";
import {
    isOrganizationMemberActivity,
    OrganizationActivityRow,
} from "./organizationActivityRow";

interface BuildOrganizationActivityDetailItemsParams {
    diff: AuditLogDiff[];
    clientNamesById: ReadonlyMap<string, string>;
}

export interface OrganizationActivityDescriptionInput {
    changeTypes: AuditLogChangeType[];
    resourceType: ResourceType;
    fields: string[];
    count: number;
}

export interface OrganizationActivityDetailItem {
    id: string;
    fieldLabel: string;
    previous: string;
    modified: string;
}

const UNKNOWN_CLIENT_NAME = "Unknown client";
const EMPTY_DETAIL_VALUE = "-";

const FIELD_ACTIVITY_LABELS: Readonly<Record<string, string>> = {
    role: "role",
    assignToNewClients: "default client access",
    clientWorkspaceIds: "client access",
};

export function getOrganizationActivityDescriptionParts({
    changeTypes,
    resourceType,
    fields,
    count,
}: OrganizationActivityDescriptionInput): ActivityDescriptionParts {
    const counter = pluralizeWithCount("member", count);
    const [changeType = AuditLogChangeType.UPDATE] = changeTypes;

    switch (changeType) {
        case AuditLogChangeType.CREATE:
            return {
                prefix:
                    resourceType === ResourceType.ORGANIZATION_INVITE
                        ? "Invited"
                        : "Added",
                counter,
                suffix: "",
            };
        case AuditLogChangeType.UPDATE: {
            const changedFieldLabels = fields.flatMap((field) => {
                const label = FIELD_ACTIVITY_LABELS[field];
                return label ? [label] : [];
            });
            const prefix =
                changedFieldLabels.length === 1
                    ? `Updated ${changedFieldLabels[0]} for`
                    : "Updated";
            return {
                prefix,
                counter,
                suffix: "",
            };
        }
        case AuditLogChangeType.DELETE:
            return {
                prefix: "Removed",
                counter,
                suffix: "",
            };
        case AuditLogChangeType.DOWNLOAD:
            return {
                prefix: "Downloaded",
                counter,
                suffix: "",
            };
        default: {
            const unhandledChangeType: never = changeType;
            return unhandledChangeType;
        }
    }
}

export function getOrganizationActivityRowDescriptionParts(
    row: OrganizationActivityRow,
): ActivityDescriptionParts {
    return isOrganizationMemberActivity(row)
        ? getOrganizationActivityDescriptionParts(row)
        : getActivityDescriptionParts(row);
}

export function describeOrganizationActivityAction(
    row: OrganizationActivityRow,
): string {
    const parts = getOrganizationActivityRowDescriptionParts(row);
    return [parts.prefix, parts.counter, parts.suffix]
        .filter(Boolean)
        .join(" ");
}

interface OrganizationActivityInviteEmailRow {
    resourceType: ResourceType;
    diff: AuditLogDiff[];
}

export function getOrganizationActivityInviteEmail({
    resourceType,
    diff,
}: OrganizationActivityInviteEmailRow): string | null {
    if (resourceType !== ResourceType.ORGANIZATION_INVITE) {
        return null;
    }

    const email = diff.find(({ field }) => field === "email")?.after.value;
    return typeof email === "string" ? email : null;
}

export function getOrganizationActivityRoleLabel(diff: AuditLogDiff[]): string {
    const roleDiff = diff.find(({ field }) => field === "role");
    if (!roleDiff) {
        return EMPTY_DETAIL_VALUE;
    }

    return (
        toRoleLabel(roleDiff.after) ??
        toRoleLabel(roleDiff.before) ??
        EMPTY_DETAIL_VALUE
    );
}

export function buildOrganizationActivityDetailItems({
    diff,
    clientNamesById,
}: BuildOrganizationActivityDetailItemsParams): OrganizationActivityDetailItem[] {
    const toClientNamesLabel = (ref: RefValue): string | null => {
        const ids = toStringArray(ref);
        if (ids.length === 0) {
            return null;
        }
        return ids
            .map((id) => clientNamesById.get(id) ?? UNKNOWN_CLIENT_NAME)
            .join(", ");
    };

    return diff.flatMap(
        ({ field, before, after }): OrganizationActivityDetailItem[] => {
            switch (field) {
                case "role": {
                    const previous = toRoleLabel(before);
                    const modified = toRoleLabel(after);
                    if (previous === null && modified === null) {
                        return [];
                    }
                    return [
                        {
                            id: field,
                            fieldLabel: "Role",
                            previous: previous ?? EMPTY_DETAIL_VALUE,
                            modified: modified ?? EMPTY_DETAIL_VALUE,
                        },
                    ];
                }
                case "assignToNewClients": {
                    const previous = toToggleLabel(before);
                    const modified = toToggleLabel(after);
                    if (previous === null && modified === null) {
                        return [];
                    }
                    return [
                        {
                            id: field,
                            fieldLabel: "Default client access",
                            previous: previous ?? EMPTY_DETAIL_VALUE,
                            modified: modified ?? EMPTY_DETAIL_VALUE,
                        },
                    ];
                }
                case "clientWorkspaceIds": {
                    const previous = toClientNamesLabel(before);
                    const modified = toClientNamesLabel(after);
                    if (previous === null && modified === null) {
                        return [];
                    }
                    return [
                        {
                            id: field,
                            fieldLabel: "Client access",
                            previous: previous ?? EMPTY_DETAIL_VALUE,
                            modified: modified ?? EMPTY_DETAIL_VALUE,
                        },
                    ];
                }
                default:
                    return [];
            }
        },
    );
}

function toRoleLabel({ value }: RefValue): string | null {
    const role = Object.values(OrganizationUserRole).find(
        (candidate) => candidate === value,
    );
    return role ? ORGANIZATION_ROLES_LABELS[role] : null;
}

function toToggleLabel({ value }: RefValue): string | null {
    if (typeof value !== "boolean") {
        return null;
    }
    return value ? "On" : "Off";
}
